import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import { useAuth } from '../hooks/useAuth.tsx';
import { http } from '../lib/http';
import RobloxLoginButton from './RobloxLoginButton.tsx';
import loginBackground from '../assets/LoginBackground.png';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const LoginScreen: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Show/hide traditional login forms
  const [showTraditionalLogin, setShowTraditionalLogin] = useState(false);
  const [showTraditionalRegister, setShowTraditionalRegister] = useState(false);
  
  // Check for success/error messages in URL (for OAuth redirects)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    // Handle success messages
    const successParam = params.get('success');
    if (successParam === 'registration_pending') {
      setSuccess('Registration successful! Your account is pending approval by an administrator.');
      setTabValue(0); // Switch to login tab
    }
    
    // Handle error messages
    const errorParam = params.get('error');
    if (errorParam) {
      let errorMessage = 'Authentication failed';
      let targetTab = 0; // Default to login tab
      
      switch (errorParam) {
        case 'not_registered':
          errorMessage = 'No account found. Please register first using the Register tab.';
          targetTab = 1; // Switch to register tab
          break;
        case 'duplicate_roblox_account':
          errorMessage = 'This Roblox account is already registered. Please use the Login tab to sign in, or contact an administrator if you need help.';
          targetTab = 0; // Switch to login tab
          break;
        case 'pending_approval':
          errorMessage = 'Your account is pending approval. Please contact an administrator.';
          break;
        case 'account_rejected':
          errorMessage = 'Your account has been rejected. Please contact an administrator.';
          break;
        case 'oauth_failed':
          errorMessage = 'Roblox authentication failed. Please try again.';
          break;
        case 'no_code':
          errorMessage = 'No authorization code received from Roblox.';
          break;
        default:
          errorMessage = `Authentication error: ${errorParam}`;
      }
      
      setError(errorMessage);
      setTabValue(targetTab);
    }
    
    // Clear URL parameters after displaying message
    if (successParam || errorParam) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  
  // Registration form state
  const [regUsername, setRegUsername] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [regRobloxName, setRegRobloxName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showResetDialog, setShowResetDialog] = useState(false);
  
  const { login } = useAuth();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const success = await login(username, password);
      if (!success) {
        setError('Invalid username or password');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (regPassword !== regConfirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (regPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    // Additional password strength validation
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(regPassword)) {
      setError('Password must contain at least one lowercase letter, one uppercase letter, and one number');
      setLoading(false);
      return;
    }

    try {
      const response = await http.post('/auth/register', {
        username: regUsername,
        password: regPassword,
        robloxName: regRobloxName,
        email: regEmail
      });

      if (response.data.success) {
        setSuccess('Registration successful! Your account is pending approval.');
        setRegUsername('');
        setRegPassword('');
        setRegConfirmPassword('');
        setRegRobloxName('');
        setRegEmail('');
        setTabValue(0); // Switch to login tab
      }
    } catch (err: any) {
      const errorMsg = typeof err.response?.data?.error === 'string'
        ? err.response.data.error
        : 'Registration failed. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };


  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    // Additional password strength validation
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      setError('Password must contain at least one lowercase letter, one uppercase letter, and one number');
      setLoading(false);
      return;
    }

    try {
      const response = await http.post('/auth/reset-password', {
        token: resetToken,
        newPassword: newPassword
      });

      if (response.data.success) {
        setSuccess('Password reset successfully! You can now log in.');
        setShowResetDialog(false);
        setResetToken('');
        setNewPassword('');
        setConfirmNewPassword('');
        setTabValue(0); // Switch to login tab
      }
    } catch (err: any) {
      const errorMsg = typeof err.response?.data?.error === 'string'
        ? err.response.data.error
        : 'Password reset failed. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
  };

  return (
    <Container 
      disableGutters 
      maxWidth={false}
      sx={{
        background: `
          linear-gradient(135deg, rgba(26, 26, 26, 0.85) 0%, rgba(45, 45, 45, 0.85) 100%),
          url(${loginBackground})
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh',
        width: '100vw',
        padding: 0, // Ensure no padding from Container
      }}
    >
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 500 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ color: '#3c8cff', fontWeight: 'bold', mb: 1 }}
              >
                Firestone CAD
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: '#b4b9c0', mb: 0.5 }}
              >
                Emergency Dispatch System
              </Typography>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange} centered>
                <Tab label="Login" />
                <Tab label="Register" />
              </Tabs>
            </Box>

            {error && (
              <Alert severity="error" sx={{ m: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ m: 2 }}>
                {success}
              </Alert>
            )}

            <TabPanel value={tabValue} index={0}>
              {/* Primary: Roblox OAuth Login */}
              <Box sx={{ mb: 3 }}>
                <RobloxLoginButton 
                  fullWidth 
                  onError={(error) => setError(error)}
                  disabled={loading}
                  variant="contained"
                />
              </Box>

              {/* Secondary: Traditional Login (Collapsible) */}
              {!showTraditionalLogin ? (
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Typography sx={{ color: '#6a6f78', fontSize: '0.8rem', mb: 1 }}>
                    or
                  </Typography>
                  <Button
                    variant="text"
                    onClick={() => setShowTraditionalLogin(true)}
                    sx={{
                      color: '#8a8f98',
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      textDecoration: 'underline',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: '#b4b9c0',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Use username and password
                  </Button>
                </Box>
              ) : (
                <>
                  <Divider sx={{ my: 2 }}>
                    <Typography sx={{ color: '#b4b9c0', fontSize: '0.875rem' }}>USERNAME/PASSWORD</Typography>
                  </Divider>
                  
                  <form onSubmit={handleLogin}>
                    <TextField
                      fullWidth
                      label="Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      margin="normal"
                      required
                      autoComplete="username"
                      autoFocus
                    />
                    <TextField
                      fullWidth
                      label="Password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      margin="normal"
                      required
                      autoComplete="current-password"
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      sx={{
                        mt: 2,
                        mb: 1,
                        py: 1.5,
                        backgroundColor: '#3c8cff',
                        '&:hover': {
                          backgroundColor: '#2a7ae9',
                        },
                      }}
                    >
                      {loading ? 'Signing In...' : 'Sign In'}
                    </Button>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => setShowTraditionalLogin(false)}
                      sx={{
                        color: '#6a6f78',
                        textTransform: 'none',
                        fontSize: '0.85rem',
                        mt: 1,
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: '#8a8f98',
                        },
                      }}
                    >
                      ← Back to Roblox login
                    </Button>
                  </form>
                </>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {/* Primary: Roblox OAuth Registration */}
              <Box sx={{ mb: 3 }}>
                <RobloxLoginButton 
                  fullWidth 
                  flow="register"
                  onError={(error) => setError(error)}
                  disabled={loading}
                  variant="contained"
                />
                <Typography sx={{ mt: 1, fontSize: '0.75rem', color: '#b4b9c0', textAlign: 'center' }}>
                  Registration requires admin approval
                </Typography>
              </Box>

              {/* Secondary: Traditional Registration (Collapsible) */}
              {!showTraditionalRegister ? (
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Typography sx={{ color: '#6a6f78', fontSize: '0.8rem', mb: 1 }}>
                    or
                  </Typography>
                  <Button
                    variant="text"
                    onClick={() => setShowTraditionalRegister(true)}
                    sx={{
                      color: '#8a8f98',
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      textDecoration: 'underline',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: '#b4b9c0',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Use username and password
                  </Button>
                </Box>
              ) : (
                <>
                  <Divider sx={{ my: 2 }}>
                    <Typography sx={{ color: '#b4b9c0', fontSize: '0.875rem' }}>USERNAME/PASSWORD REGISTRATION</Typography>
                  </Divider>
                  
                  <form onSubmit={handleRegister}>
                    <TextField
                      fullWidth
                      label="Username"
                      value={regUsername}
                      onChange={(e) => setRegUsername(e.target.value)}
                      margin="normal"
                      required
                      autoComplete="username"
                    />
                    <TextField
                      fullWidth
                      label="Roblox Name"
                      value={regRobloxName}
                      onChange={(e) => setRegRobloxName(e.target.value)}
                      margin="normal"
                      required
                    />
                    <TextField
                      fullWidth
                      label="Email (Optional)"
                      type="email"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      margin="normal"
                    />
                    <TextField
                      fullWidth
                      label="Password"
                      type="password"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      margin="normal"
                      required
                      autoComplete="new-password"
                    />
                    <TextField
                      fullWidth
                      label="Confirm Password"
                      type="password"
                      value={regConfirmPassword}
                      onChange={(e) => setRegConfirmPassword(e.target.value)}
                      margin="normal"
                      required
                      autoComplete="new-password"
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      sx={{
                        mt: 2,
                        mb: 1,
                        py: 1.5,
                        backgroundColor: '#3c8cff',
                        '&:hover': {
                          backgroundColor: '#2a7ae9',
                        },
                      }}
                    >
                      {loading ? 'Registering...' : 'Register'}
                    </Button>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => setShowTraditionalRegister(false)}
                      sx={{
                        color: '#6a6f78',
                        textTransform: 'none',
                        fontSize: '0.85rem',
                        mt: 1,
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: '#8a8f98',
                        },
                      }}
                    >
                      ← Back to Roblox registration
                    </Button>
                  </form>
                </>
              )}
            </TabPanel>

          </CardContent>
          
          {/* Footer with legal links */}
          <Box
            sx={{
              px: 3,
              pb: 2,
              pt: 1,
              borderTop: '1px solid #40444b',
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Link
              to="/legal/privacy"
              style={{
                color: '#8a8f98',
                fontSize: '0.8rem',
                textDecoration: 'none',
              }}
            >
              Privacy Policy
            </Link>
            <Typography sx={{ color: '#464a52', fontSize: '0.8rem' }}>•</Typography>
            <Link
              to="/legal/terms"
              style={{
                color: '#8a8f98',
                fontSize: '0.8rem',
                textDecoration: 'none',
              }}
            >
              Terms of Service
            </Link>
          </Box>
        </Card>
      </Box>

      {/* Password Reset Dialog */}
      <Dialog open={showResetDialog} onClose={() => setShowResetDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <form onSubmit={handleResetPassword}>
          <DialogContent>
            <TextField
              fullWidth
              label="Reset Token"
              value={resetToken}
              onChange={(e) => setResetToken(e.target.value)}
              margin="normal"
              required
              disabled
            />
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              margin="normal"
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowResetDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default LoginScreen;
